import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams , useNavigate } from 'react-router-dom';

const ProsCons = () => {
    const [entries, setEntries] = useState([{ type: '', heading: '' }]);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const { id } = useParams();
    const [successMessage, setSuccessMessage] = useState('');
    const navigate = useNavigate();


    // Fetch existing Pros and Cons from API when the component mounts
    useEffect(() => {
        const fetchProsCons = async () => {
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_API_KEY}career/fetchProsCons`,
                    new URLSearchParams({ career_id: id }),
                    {
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    }
                );
                if (response.data.success) {
                    setEntries(response.data.data.map(item => (
                        { 
                            type: item.select_type, 
                            heading: item.heading ,
                            id : item.id
                        }
                        )));
                } else {
                    setErrorMessage('Failed to load Pros and Cons data.');
                }
            } catch (error) {
                // setErrorMessage('Error fetching Pros and Cons data.');
            } finally {
                setLoading(false);
            }
        };

        fetchProsCons();
    }, []);

    const handleInputChange = (index, event) => {
        const values = [...entries];
        values[index][event.target.name] = event.target.value;
        setEntries(values);
    };

    const handleAddMore = () => {
        const prosCount = entries.filter(entry => entry.type === 'Pros').length;
        const consCount = entries.filter(entry => entry.type === 'Cons').length;

        if (prosCount < 4 || consCount < 4) {
            setEntries([...entries, { type: '', heading: '' }]);
            setErrorMessage('');
        } else {
            setErrorMessage('You cannot add more than 4 Pros and 4 Cons.');
        }
    };

    const handleRemove = async (index) => {
        const values = [...entries];
        const entryToRemove = values[index];
    
        if (entryToRemove.id) {
            try {
                // Prepare the API call for removing the entry
                const options = {
                    method: 'POST',
                    url: `${process.env.REACT_APP_API_KEY}career/removeProsCons`,
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Cookie': '',
                    },
                    data: new URLSearchParams({
                        'id': entryToRemove.id, // Pass the ID of the Pros/Cons to remove
                    }),
                };
    
                // Make the API request using axios
                const response = await axios(options);
    
                if (response.data.success) {
                    console.log('Entry removed successfully');
                    values.splice(index, 1); // Remove the entry from the state
                    setEntries(values); // Update the state
                } else {
                    setErrorMessage('Failed to remove the entry.');
                }
            } catch (error) {
                console.error('Error removing entry:', error);
                setErrorMessage('An error occurred while removing the entry.');
            }
        } else {
            // If no ID is present, simply remove from state without API call
            values.splice(index, 1);
            setEntries(values);
        }
    };
    

    const handleSave = async () => {
        const prosCount = entries.filter(entry => entry.type === 'Pros').length;
        const consCount = entries.filter(entry => entry.type === 'Cons').length;
    
        if (prosCount < 2 || consCount < 2) {
            setErrorMessage('You must add at least 2 Pros and 2 Cons.');
            return;
        }
    
        if (prosCount > 4 || consCount > 4) {
            setErrorMessage('You cannot have more than 4 Pros and 4 Cons.');
            return;
        }
    
        setErrorMessage('');
        setSuccessMessage(''); // Clear success message before saving
    
        try {
            for (let entry of entries) {
                const options = {
                    method: 'POST',
                    url: `${process.env.REACT_APP_API_KEY}career/prosAndCons`,
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    data: new URLSearchParams({
                        'career_id': id,
                        'select_type': entry.type,
                        'heading': entry.heading,
                        'id': entry.id || '0',
                        'isEdit': entry.id ? '1' : '0'
                    })
                };
    
                await axios(options);
            }
    
            setSuccessMessage('All entries saved successfully!');
            console.log("All entries saved successfully!");
            window.location.href = `/career-deep-dive/${id}?tab=famous_personality`;
            
        } catch (error) {
            console.error("Error saving entries: ", error);
            setErrorMessage('An error occurred while saving the entries.');
        }
    };
    

    if (loading) {
        return <p>Loading...</p>;
    }

    const handleCancel = () => {
        navigate("/career-list");
    };

    return (
        <div className="bg-white radius-10 p-4 p-sm-5 shadow">
            <div className="row g-4">
                <div className="col-md-12">
                    <h2 className="fw-semibold font-24">Pros and Cons</h2>
                </div>

                {entries.map((entry, index) => (
                    <React.Fragment key={index}>
                        <div className="col-md-5">
                            <label htmlFor={`type-${index}`} className="form-label w-100 font-500 mb-1">Select Type</label>
                            <select 
                                id={`type-${index}`} 
                                name="type" 
                                className="form-select height-45" 
                                value={entry.type} 
                                onChange={(event) => handleInputChange(index, event)}
                            >
                                <option value="">Select</option>
                                <option value="Pros">Pros</option>
                                <option value="Cons">Cons</option>
                            </select>
                        </div>

                        <div className="col-md-5">
                            <label htmlFor={`heading-${index}`} className="form-label w-100 font-500 mb-1">Heading</label>
                            <input 
                                id={`heading-${index}`} 
                                name="heading" 
                                type="text" 
                                className="form-control height-45" 
                                placeholder="Ex: Heading" 
                                maxLength="50" 
                                value={entry.heading} 
                                onChange={(event) => handleInputChange(index, event)} 
                            />
                        </div>

                        <div className="col-md-2 d-flex align-items-end">
                            {entries.length > 1 && (
                                <button 
                                    type="button" 
                                    className="btn btn-danger height-45" 
                                    onClick={() => handleRemove(index)}
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                    </React.Fragment>
                ))}

                <div className="col-12">
                    <hr className="mt-0" />
                    {(entries.filter(entry => entry.type === '').length > 0 || entries.length >= 8) ? null : (
                        <button className="btn-green pe-3" onClick={handleAddMore}>
                            <i className="icon-plus me-2"></i> Add More
                        </button>
                    )}
                </div>

                {errorMessage && (
                    <div className="col-12">
                        <p className="text-danger">{errorMessage}</p>
                    </div>
                )}

                <div className="col-md-12">
                    <div className="action_btns mt-3">
                        <div className="row g-2 align-items-center justify-content-center">
                            <div className="col-auto">
                                <button type="button" className="btn btn-green height-45 px-4" onClick={handleSave}>
                                    <span>Save and Next</span>
                                </button>
                            </div>
                            <div className="col-auto">
                                <button type="button" className="btn btn-border-gray height-45 px-5" onClick={handleCancel}>
                                    <span>Cancel </span>
                                </button>
                            </div>
                        </div>
                    </div>
                    {successMessage && (
                        <div className="alert alert-success mt-3">{successMessage}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProsCons;
